import BlogInertiaController from '@/actions/App/Http/Controllers/BlogInertiaController';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { formatBlogDate, getBlogImageUrl } from '@/lib/blog';
import { cn } from '@/lib/utils';
import { Post } from '@/types/model';
import { Link } from '@inertiajs/react';
import { ArrowRight, Calendar, Clock } from 'lucide-react';

interface BlogPostCardProps {
  post: Post;
  variant?: 'default' | 'compact';
  className?: string;
}

export function BlogPostCard({ post, variant = 'default', className }: BlogPostCardProps) {
  const isCompact = variant === 'compact';

  return (
    <Card
      className={cn('group break-inside-avoid overflow-hidden transition-all duration-300 hover:shadow-lg', isCompact ? 'mb-4' : 'mb-6', className)}
    >
      <Link href={BlogInertiaController.show(post.slug).url} className="block">
        {/* Featured Image */}
        <div className={cn('relative overflow-hidden', isCompact ? 'aspect-video' : 'aspect-[4/3]')}>
          <img
            src={getBlogImageUrl(post.featured_image, 'blog')}
            alt={post.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {/* Category Badge Overlay */}
          <Badge
            variant="secondary"
            className={cn('bg-background/95 absolute backdrop-blur-sm', isCompact ? 'top-2 left-2 text-xs' : 'top-4 left-4')}
          >
            {post.category?.name}
          </Badge>
        </div>

        {/* Content */}
        <CardContent className={cn(isCompact ? 'p-3' : 'p-4')}>
          {/* Date & Reading Time */}
          <div className={cn('text-muted-foreground mb-2 flex items-center gap-2', isCompact ? 'text-xs' : 'text-xs')}>
            <Calendar className={cn(isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
            <span>{formatBlogDate(post.published_at || '')}</span>
            {post.reading_time && (
              <>
                <Separator orientation="vertical" className="h-3" />
                <Clock className={cn(isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
                <span>{post.reading_time} phút đọc</span>
              </>
            )}
          </div>

          {/* Title */}
          <h3
            className={cn('group-hover:text-primary mb-2 font-bold transition-colors', isCompact ? 'line-clamp-2 text-sm' : 'line-clamp-2 text-lg')}
          >
            {post.title}
          </h3>

          {/* Excerpt (default variant only) */}
          {!isCompact && post.excerpt && <p className="text-muted-foreground mb-3 line-clamp-3 text-sm">{post.excerpt}</p>}

          {/* Read More Link */}
          {!isCompact && (
            <div className="text-primary flex items-center gap-1 text-sm font-semibold">
              Đọc thêm <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          )}
        </CardContent>
      </Link>
    </Card>
  );
}
